import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardLayout from './components/DashboardLayout';
import ProductsPage from './pages/ProductsPage';
import LandingPagesPage from './pages/LandingPagesPage';
import LandingPageEditor from './pages/LandingPageEditor';
import PremiumLPEditor from './pages/PremiumLPEditor';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import OrdersPage from './pages/OrdersPage';
import SalesReportPage from './pages/SalesReportPage';
import PaymentSettingsPage from './pages/PaymentSettingsPage';
import DownloadPage from './pages/DownloadPage';
import DashboardOverview from './pages/DashboardOverview';
import ProfilePage from './pages/ProfilePage';
import ProductCreatePage from './pages/ProductCreatePage';
import PublicLandingPage from './pages/PublicLandingPage';
import CheckoutFormsPage from './pages/CheckoutFormsPage';
import UserSettingsPage from './pages/UserSettingsPage';
import GettingStartedPage from './pages/GettingStartedPage';
import GuideTutorialPage from './pages/GuideTutorialPage';
import PlanUpgradePage from './pages/PlanUpgradePage';
import ThankYouPage from './pages/ThankYouPage';
import MarketingPage from './pages/MarketingPage';
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import TemplatePreviewPage from './pages/TemplatePreviewPage';
import LPDashboardPage from './pages/LPDashboardPage';
import PartnershipPage from './pages/PartnershipPage';
import DigitalStudioPage from './pages/DigitalStudioPage';
import CourseBuilderPage from './pages/CourseBuilderPage';
import CoursePreviewPage from './pages/CoursePreviewPage';
import CoursePortalPage from './pages/CoursePortalPage';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCreators from './pages/admin/AdminCreators';
import AdminCreatorDetail from './pages/admin/AdminCreatorDetail';
import AdminOrders from './pages/admin/AdminOrders';
import AdminAIUsage from './pages/admin/AdminAIUsage';
import AdminEmailBlast from './pages/admin/AdminEmailBlast';
import AdminWebhookLogs from './pages/admin/AdminWebhookLogs';
import AdminPromoCodes from './pages/admin/AdminPromoCodes';
import AdminPartnership from './pages/admin/AdminPartnership';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex h-screen items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={user ? <Navigate to={user.is_admin ? "/admin" : "/app"} replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/app" replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Admin Panel */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="creators" element={<AdminCreators />} />
        <Route path="creators/:creatorId" element={<AdminCreatorDetail />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="ai-usage" element={<AdminAIUsage />} />
        <Route path="email-blast" element={<AdminEmailBlast />} />
        <Route path="webhook-logs" element={<AdminWebhookLogs />} />
        <Route path="promo-codes" element={<AdminPromoCodes />} />
        <Route path="partnership" element={<AdminPartnership />} />
      </Route>

      {/* Dashboard */}
      <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardOverview />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="getting-started" element={<GettingStartedPage />} />
        <Route path="getting-started/:tutorialId" element={<GuideTutorialPage />} />
        <Route path="plan" element={<PlanUpgradePage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/new" element={<ProductCreatePage />} />
        <Route path="landing-pages" element={<LandingPagesPage />} />
        <Route path="landing-pages/:id/edit" element={<LandingPageEditor />} />
        <Route path="landing-pages/:id/premium" element={<PremiumLPEditor />} />
        <Route path="checkout-forms" element={<CheckoutFormsPage />} />
        <Route path="studio" element={<DigitalStudioPage />} />
        <Route path="studio/course/new" element={<CourseBuilderPage />} />
        <Route path="studio/course/:courseId" element={<CourseBuilderPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="sales-report" element={<SalesReportPage />} />
        <Route path="payment-settings" element={<PaymentSettingsPage />} />
        <Route path="user-settings" element={<UserSettingsPage />} />
      </Route>

      {/* Public Store */}
      {/* Marketing Page */}
      <Route path="/" element={<MarketingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/template-preview/:templateId" element={<TemplatePreviewPage />} />
      <Route path="/course-preview/:courseId" element={<CoursePreviewPage />} />
      <Route path="/course/:courseId" element={<CoursePortalPage />} />
      <Route path="/lpdashboard" element={<LPDashboardPage />} />
      <Route path="/partnership" element={<PartnershipPage />} />

      {/* Public Landing Pages - new format: /{storeName}/{pageSlug} */}
      <Route path="/store/:pageId" element={<PublicLandingPage />} />
      <Route path="/thank-you/:sessionId" element={<ThankYouPage />} />
      <Route path="/download/:token" element={<DownloadPage />} />
      <Route path="/:storeName/:pageSlug" element={<PublicLandingPage />} />

      {/* Default */}
      <Route path="*" element={<Navigate to={user ? "/app" : "/"} replace />} />
    </Routes>
  );
}

export default function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <Router>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </Router>
    </GoogleOAuthProvider>
  );
}
